import { motion } from "framer-motion";
import { Service } from "@/lib/index";
import { springPresets, hoverLift } from "@/lib/motion";

interface ServiceCardProps {
  service: Service;
}

export function ServiceCard({ service }: ServiceCardProps) {
  const IconComponent = service.icon as any;

  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="group relative bg-gradient-to-br from-card via-card to-primary/5 border border-border/50 rounded-3xl p-8 transition-all duration-300 hover:border-primary/30 overflow-hidden"
      style={{
        boxShadow: "0 10px 40px -10px color-mix(in srgb, var(--primary) 20%, transparent)"
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="absolute -right-8 -top-8 w-32 h-32 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-all duration-500" />
      
      <div className="relative z-10">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:from-primary/30 group-hover:to-primary/20 transition-all duration-300">
          {typeof IconComponent === 'string' ? (
            <span className="text-2xl">{IconComponent}</span>
          ) : (
            <IconComponent className="w-8 h-8 text-primary" />
          )}
        </div>
        
        <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
          {service.title}
        </h3>
        
        <p className="text-muted-foreground leading-relaxed text-sm">
          {service.description}
        </p>
      </div>
    </motion.div>
  );
}

interface ContactCardProps {
  icon: React.ReactNode;
  title: string;
  content: string;
  link?: string;
}

export function ContactCard({ icon, title, content, link }: ContactCardProps) {
  const CardContent = (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="group bg-card border border-border rounded-2xl p-6 transition-all duration-200"
      style={{
        boxShadow: "0 8px 30px -6px color-mix(in srgb, var(--primary) 15%, transparent)"
      }}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors duration-200">
          <div className="text-primary">{icon}</div>
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-muted-foreground mb-1">
            {title}
          </h4>
          <p className="text-foreground font-medium break-words group-hover:text-primary transition-colors duration-200">
            {content}
          </p>
        </div>
      </div>
    </motion.div>
  );

  if (link) {
    return (
      <a href={link} target="_blank" rel="noopener noreferrer" className="block">
        {CardContent}
      </a>
    );
  }

  return CardContent;
}